import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Building2,
  Search,
  Check,
  Loader2,
  AlertCircle,
  Mail,
  Phone,
  MapPin,
  Info,
  Plus,
  Edit2,
  ChevronDown,
} from "lucide-react";
import { toast } from "react-toastify";
import organizerService from "../../../../../api/OrganizerService";
import CreateEditOrganizerModal from "./CreateEditOrganizerModal";

const StepOneOrganizerSelection = ({ selectedOrganizer, onSelect, onNext }) => {
  const [organizers, setOrganizers] = useState([]);
  const [filteredOrganizers, setFilteredOrganizers] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [organizerToEdit, setOrganizerToEdit] = useState(null);

  const ITEMS_PER_PAGE = 24;
  const [displayedCount, setDisplayedCount] = useState(ITEMS_PER_PAGE);

  // Fetch organizers function
  const fetchOrganizers = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await organizerService.getOrganizers();
      if (response.data.status_code === 200) {
        const organizersList = Array.isArray(response.data.data)
          ? response.data.data
          : response.data.data || [];
        setOrganizers(organizersList);
        setFilteredOrganizers(organizersList);
        return organizersList;
      } else {
        setError(response.data.reason || "Failed to load organizers");
        toast.error(response.data.reason || "Failed to load organizers");
        return [];
      }
    } catch (err) {
      const errorMessage = "An error occurred while loading organizers";
      setError(errorMessage);
      toast.error(errorMessage);
      return [];
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (organizers.length > 0 && !searchQuery.trim()) {
      setDisplayedCount(ITEMS_PER_PAGE);
    }
  }, [organizers.length, searchQuery]);

  useEffect(() => {
    fetchOrganizers();
  }, [fetchOrganizers]);

  useEffect(() => {
    if (!searchQuery.trim()) {
      setFilteredOrganizers(organizers);
    } else {
      const query = searchQuery.toLowerCase();
      const filtered = organizers.filter(
        (org) =>
          org.name?.toLowerCase().includes(query) ||
          org.email?.toLowerCase().includes(query) ||
          org.city?.toLowerCase().includes(query) ||
          org.country?.toLowerCase().includes(query) ||
          org.phone_number?.includes(query)
      );
      setFilteredOrganizers(filtered);
    }
    setDisplayedCount(ITEMS_PER_PAGE);
  }, [searchQuery, organizers]);

  const displayedOrganizers = useMemo(() => {
    if (searchQuery.trim()) {
      return filteredOrganizers;
    }
    return filteredOrganizers.slice(0, displayedCount);
  }, [filteredOrganizers, displayedCount, searchQuery]);

  const hasMore = useMemo(() => {
    if (searchQuery.trim()) {
      return false;
    }
    return displayedCount < filteredOrganizers.length;
  }, [filteredOrganizers.length, displayedCount, searchQuery]);

  const handleLoadMore = () => {
    setDisplayedCount((prev) => prev + ITEMS_PER_PAGE);
  };

  const handleOrganizerSelect = (organizer) => {
    onSelect(organizer);
  };

  const handleNext = () => {
    if (!selectedOrganizer) {
      toast.error("Please select an organizer to continue");
      return;
    }
    onNext();
  };

  const handleCreateOrganizer = () => {
    setOrganizerToEdit(null);
    setIsModalOpen(true);
  };

  const handleEditOrganizer = (e, organizer) => {
    e.stopPropagation();
    setOrganizerToEdit(organizer);
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setOrganizerToEdit(null);
  };

  const handleModalSuccess = async (organizer, isEdit) => {
    const updatedList = await fetchOrganizers();
    if (organizer && updatedList) {
      const foundOrganizer = updatedList.find(
        (org) =>
          (organizer.slug && org.slug === organizer.slug) ||
          org.name === organizer.name
      );
      const organizerToSelect = foundOrganizer || organizer;
      onSelect(organizerToSelect);

      toast.success(
        isEdit
          ? "Organizer updated and selected!"
          : "Organizer created and selected!"
      );
    }
  };

  const getOrganizerInitials = (organizer) => {
    if (!organizer.name) return "OR";
    return organizer.name
      .split(" ")
      .map((name) => name[0])
      .join("")
      .toUpperCase()
      .substring(0, 2);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="p-4 bg-white rounded-xl border border-gray-100 shadow-sm sm:rounded-2xl">
        <div className="flex items-center space-x-3 mb-2">
          <div className="p-2 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg shadow">
            <Building2 className="w-6 h-6 text-white" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-gray-900 sm:text-2xl">
              Select Event Organizer
            </h2>
            <p className="mt-0.5 text-xs text-gray-600 sm:text-sm">
              Choose the organizer hosting this event
            </p>
          </div>
        </div>

        {/* Info Banner */}
        <div className="p-3 mt-4 bg-blue-50 rounded-lg border border-blue-200">
          <div className="flex items-start space-x-2">
            <Info className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
            <div className="text-xs text-blue-800">
              <p className="mb-0.5 font-semibold">💡 Selection Guide:</p>
              <ul className="space-y-0.5 text-xs list-disc list-inside">
                <li>You can only select one organizer per event</li>
                <li>Use the search bar to quickly find organizers</li>
                <li>Click on an organizer card to select it</li>
                <li>
                  Create new organizers or edit existing ones without leaving
                  this page
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Search Bar and Create Button */}
      <div className="flex flex-col gap-3 sm:flex-row">
        <div className="flex-1 p-4 bg-white rounded-xl border border-gray-100 shadow-sm sm:rounded-2xl">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 w-5 h-5 text-gray-400 -translate-y-1/2" />
            <input
              type="text"
              placeholder="Search organizers by name, email, city, or country..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-3 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all bg-gray-50 focus:bg-white"
            />
          </div>
        </div>
        <button
          onClick={handleCreateOrganizer}
          className="flex items-center justify-center space-x-2 px-6 py-3 text-sm font-semibold text-white bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 rounded-xl transition-all shadow-md hover:shadow-lg"
        >
          <Plus className="w-4 h-4" />
          <span>New Organizer</span>
        </button>
      </div>

      {/* Loading State */}
      {isLoading && (
        <div className="flex flex-col justify-center items-center py-16 bg-white rounded-xl border border-gray-100 shadow-sm">
          <Loader2 className="w-8 h-8 mb-4 text-blue-600 animate-spin" />
          <p className="text-sm font-medium text-gray-600">
            Loading organizers...
          </p>
        </div>
      )}

      {/* Error State */}
      {error && !isLoading && (
        <div className="p-4 bg-red-50 rounded-xl border border-red-200">
          <div className="flex items-start space-x-2">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-red-900">
                Error loading organizers
              </p>
              <p className="mt-1 text-xs text-red-700">{error}</p>
              <button
                onClick={fetchOrganizers}
                className="mt-2 px-3 py-1.5 text-xs font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors"
              >
                Try Again
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Organizers Grid */}
      {!isLoading && !error && (
        <>
          <div className="flex justify-between items-center mb-4">
            <p className="text-sm font-medium text-gray-600">
              {filteredOrganizers.length === 0
                ? "No organizers found"
                : searchQuery.trim()
                ? `Showing ${displayedOrganizers.length} of ${
                    filteredOrganizers.length
                  } ${filteredOrganizers.length === 1 ? "result" : "results"}`
                : `Showing ${displayedOrganizers.length} of ${
                    filteredOrganizers.length
                  } ${
                    filteredOrganizers.length === 1 ? "organizer" : "organizers"
                  }`}
            </p>
            <div className="flex items-center space-x-3">
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery("")}
                  className="text-xs font-medium text-blue-600 hover:text-blue-700 transition-colors"
                >
                  Clear search
                </button>
              )}
            </div>
          </div>

          {filteredOrganizers.length === 0 ? (
            <div className="py-16 text-center bg-white rounded-xl border border-gray-100 shadow-sm">
              <div className="inline-flex justify-center items-center mb-4 w-16 h-16 bg-gray-100 rounded-full">
                <Building2 className="w-8 h-8 text-gray-400" />
              </div>
              <h3 className="mb-2 text-lg font-semibold text-gray-900">
                No organizers found
              </h3>
              <p className="mx-auto max-w-sm text-sm text-gray-600">
                {searchQuery
                  ? "Try adjusting your search query"
                  : "No organizers are available at the moment"}
              </p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {displayedOrganizers.map((organizer) => {
                  const isSelected = selectedOrganizer?.slug === organizer.slug;
                  return (
                    <div
                      key={organizer.slug}
                      className={`relative p-4 bg-white rounded-xl border-2 transition-all duration-200 hover:shadow-lg group ${
                        isSelected
                          ? "border-blue-500 bg-blue-50 shadow-md scale-[1.02]"
                          : "border-gray-200 hover:border-blue-300 hover:bg-gray-50"
                      }`}
                    >
                      <button
                        onClick={() => handleOrganizerSelect(organizer)}
                        className="w-full text-left"
                      >
                        <div className="flex items-start space-x-3">
                          {/* Logo */}
                          <div className="flex-shrink-0">
                            {organizer.logo ? (
                              <img
                                src={organizer.logo}
                                alt={organizer.name}
                                className="object-cover w-16 h-16 rounded-xl"
                                onError={(e) => {
                                  e.target.style.display = "none";
                                  e.target.nextSibling.style.display = "flex";
                                }}
                              />
                            ) : null}
                            <div
                              style={{
                                display: organizer.logo ? "none" : "flex",
                              }}
                              className="flex overflow-hidden justify-center items-center w-16 h-16 bg-gradient-to-br from-blue-400 to-blue-600 rounded-xl shadow-md"
                            >
                              <span className="text-2xl font-bold text-white">
                                {getOrganizerInitials(organizer)}
                              </span>
                            </div>
                          </div>

                          {/* Details */}
                          <div className="flex-1 min-w-0">
                            <div className="flex justify-between items-start mb-2">
                              <h3 className="text-base font-bold text-gray-900 truncate">
                                {organizer.name}
                              </h3>
                              {isSelected && (
                                <div className="flex-shrink-0 ml-2 p-1 bg-blue-600 rounded-full">
                                  <Check className="w-4 h-4 text-white" />
                                </div>
                              )}
                            </div>

                            <div className="space-y-1.5">
                              <div className="flex items-center text-xs text-gray-600">
                                <Mail className="w-3 h-3 mr-1.5 text-gray-400 flex-shrink-0" />
                                <span className="truncate">
                                  {organizer.email}
                                </span>
                              </div>
                              <div className="flex items-center text-xs text-gray-600">
                                <Phone className="w-3 h-3 mr-1.5 text-gray-400 flex-shrink-0" />
                                <span className="truncate">
                                  {organizer.phone_number}
                                </span>
                              </div>
                              <div className="flex items-center text-xs text-gray-600">
                                <MapPin className="w-3 h-3 mr-1.5 text-gray-400 flex-shrink-0" />
                                <span className="truncate">
                                  {organizer.city}, {organizer.country}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </button>
                      {/* Edit Button - Always visible on hover */}
                      <button
                        onClick={(e) => handleEditOrganizer(e, organizer)}
                        className="absolute top-2 right-2 p-2 text-gray-400 bg-white rounded-lg shadow-sm transition-all opacity-0 group-hover:opacity-100 hover:text-blue-600 hover:bg-blue-50 border border-gray-200"
                        title="Edit organizer"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                    </div>
                  );
                })}
              </div>

              {/* Load More Button */}
              {hasMore && (
                <div className="flex justify-center mt-6">
                  <button
                    onClick={handleLoadMore}
                    className="flex items-center space-x-2 px-6 py-3 text-sm font-semibold text-blue-600 bg-blue-50 hover:bg-blue-100 border-2 border-blue-200 rounded-xl transition-all shadow-sm hover:shadow-md"
                  >
                    <span>Load More Organizers</span>
                    <ChevronDown className="w-4 h-4" />
                  </button>
                </div>
              )}
            </>
          )}
        </>
      )}

      {/* Selected Organizer Summary & Next Button */}
      {selectedOrganizer && (
        <div className="sticky bottom-0 p-4 bg-white border-t-2 border-blue-500 rounded-xl shadow-lg sm:rounded-2xl">
          <div className="flex flex-col gap-4 sm:flex-row sm:justify-between sm:items-center">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Check className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-xs font-medium text-gray-600">
                  Selected Organizer
                </p>
                <p className="text-sm font-bold text-gray-900">
                  {selectedOrganizer.name}
                </p>
              </div>
            </div>
            <button
              onClick={handleNext}
              className="px-6 py-3 text-sm font-semibold text-white bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 rounded-lg transition-all shadow-md hover:shadow-lg flex items-center justify-center space-x-2"
            >
              <span>Continue to Next Step</span>
              <Check className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      <CreateEditOrganizerModal
        isOpen={isModalOpen}
        onClose={handleModalClose}
        organizer={organizerToEdit}
        onSuccess={handleModalSuccess}
      />
    </div>
  );
};

export default StepOneOrganizerSelection;
